import { getDistrict } from '../data/districts';
import type { Competitor, CompetitorLot } from '../models/Competitor';
import type { GameState } from '../models/GameState';
import type { GameTime } from '../models/GameTime';
import type { ParkingLot } from '../models/ParkingLot';
import { DemandEngine } from './DemandEngine';
import { ParkingLotEngine } from './ParkingLotEngine';

/** A district participant reduced to what market share depends on. */
export interface Participant {
  ownerId: string; // 'player' or competitor id
  ownerName: string;
  lotId: string;
  districtId: string;
  price: number;
  reputation: number;
  capacity: number;
}

export interface MarketShareEntry {
  ownerId: string;
  ownerName: string;
  share: number; // 0..1
}

/**
 * MarketEngine (spec section 21): splits each district's demand among all
 * lots there (player + competitors) by "attractiveness" — a blend of price,
 * reputation and size. Pure & deterministic.
 *
 * The player's lots are still micro-simulated; the competition multiplier tells
 * the DemandEngine how much of the local demand the player actually captures.
 */
export const MarketEngine = {
  /** Attractiveness weight for one participant (higher = draws more cars). */
  attractiveness(price: number, reputation: number, capacity: number): number {
    const priceFactor = DemandEngine.priceMultiplier(price); // cheaper = higher
    const repFactor = 0.5 + reputation / 200; // rep 0 -> 0.5, 100 -> 1.0
    const sizeFactor = Math.sqrt(Math.max(1, capacity)); // presence
    return priceFactor * repFactor * sizeFactor;
  },

  playerParticipant(lot: ParkingLot): Participant {
    return {
      ownerId: 'player',
      ownerName: 'You',
      lotId: lot.id,
      districtId: lot.districtId,
      price: lot.parkingPrice,
      reputation: lot.reputation,
      capacity: ParkingLotEngine.getCapacity(lot),
    };
  },

  competitorParticipant(c: Competitor, lot: CompetitorLot): Participant {
    return {
      ownerId: c.id,
      ownerName: c.name,
      lotId: lot.id,
      districtId: lot.districtId,
      price: lot.price,
      reputation: lot.reputation,
      capacity: lot.capacity,
    };
  },

  /** All participants (player + competitors) in a given district. */
  participantsIn(state: GameState, districtId: string): Participant[] {
    const result: Participant[] = [];
    for (const lot of state.parkingLots) {
      if (lot.districtId === districtId) result.push(this.playerParticipant(lot));
    }
    for (const c of state.competitors) {
      for (const lot of c.lots) {
        if (lot.districtId === districtId) {
          result.push(this.competitorParticipant(c, lot));
        }
      }
    }
    return result;
  },

  /**
   * Fraction of local demand a player lot captures vs competitors in the same
   * district (1.0 if it has no competitors there). Feeds DemandEngine.
   */
  competitionMultiplier(lot: ParkingLot, competitors: Competitor[]): number {
    const mine = this.attractiveness(
      lot.parkingPrice,
      lot.reputation,
      ParkingLotEngine.getCapacity(lot)
    );
    let rivals = 0;
    for (const c of competitors) {
      for (const cl of c.lots) {
        if (cl.districtId === lot.districtId) {
          rivals += this.attractiveness(cl.price, cl.reputation, cl.capacity);
        }
      }
    }
    const total = mine + rivals;
    return total > 0 ? mine / total : 1;
  },

  /** Market share per owner within one district. */
  districtShares(state: GameState, districtId: string): MarketShareEntry[] {
    const parts = this.participantsIn(state, districtId);
    const weights = parts.map((p) =>
      this.attractiveness(p.price, p.reputation, p.capacity)
    );
    const total = weights.reduce((s, w) => s + w, 0);

    // Aggregate by owner (an owner may have one lot per district here).
    const byOwner = new Map<string, MarketShareEntry>();
    parts.forEach((p, i) => {
      const share = total > 0 ? weights[i] / total : 0;
      const existing = byOwner.get(p.ownerId);
      if (existing) existing.share += share;
      else byOwner.set(p.ownerId, { ownerId: p.ownerId, ownerName: p.ownerName, share });
    });
    return [...byOwner.values()];
  },

  /**
   * Overall market share across the whole city, weighting each district by its
   * total demand pool at the given time (busier districts count for more).
   */
  overallShares(state: GameState, time: GameTime): MarketShareEntry[] {
    const totals = new Map<string, MarketShareEntry>();
    let grandPool = 0;

    const districtIds = new Set<string>();
    for (const lot of state.parkingLots) districtIds.add(lot.districtId);
    for (const c of state.competitors) {
      for (const lot of c.lots) districtIds.add(lot.districtId);
    }

    for (const districtId of districtIds) {
      const district = getDistrict(districtId);
      if (!district) continue;
      const pool =
        district.baseDemand * DemandEngine.dayMultiplier(district, time);
      grandPool += pool;
      for (const entry of this.districtShares(state, districtId)) {
        const weighted = entry.share * pool;
        const acc = totals.get(entry.ownerId);
        if (acc) acc.share += weighted;
        else
          totals.set(entry.ownerId, {
            ownerId: entry.ownerId,
            ownerName: entry.ownerName,
            share: weighted,
          });
      }
    }

    const result = [...totals.values()];
    if (grandPool > 0) for (const e of result) e.share /= grandPool;
    result.sort((a, b) => b.share - a.share);
    return result;
  },
};
